import type { IconButtonProps as MuiIconButtonProps } from '@mui/material';
import { IconButton as MuiIconButton, Tooltip } from '@mui/material';
import classNames from 'classnames/bind';
import React from 'react';
import type { ButtonTone } from './Button';

import styles from './IconButton.module.scss';

const cx = classNames.bind(styles);

export type IconButtonProps = Omit<MuiIconButtonProps, 'color' | 'size' | 'children'> & {
  /**
   * Accessible name, also used as the tooltip title. Required because ten of the
   * icon buttons this replaces shipped with no accessible name at all.
   */
  label: string;
  icon: React.ReactNode;
  tone?: ButtonTone;
  size?: 'sm' | 'md' | 'lg';
  iconSize?: 'sm' | 'md' | 'lg';
  shape?: 'rounded' | 'circle';
  /** Tooltip placement, or `false` to render none. */
  tooltip?: false | 'top' | 'bottom' | 'left' | 'right';
  /** Frosted background, for controls floating over photography. */
  surface?: 'none' | 'translucent';
  /** Toggled-on state, replacing hand-rolled `selected` class concatenation. */
  active?: boolean;
  type?: 'button' | 'submit' | 'reset';
};

const ICON_SIZE_CLASS = {
  sm: 'iconSm',
  md: 'iconMd',
  lg: 'iconLg',
} as const;

/**
 * Icon-only button.
 *
 * Deliberately separate from `Button` instead of a `iconOnly` flag on it: the
 * two have different a11y contracts (this one requires a `label`) and different
 * geometry, and folding them together is how prop-flag accretion starts.
 */
function IconButton({
  label,
  icon,
  tone = 'neutral',
  size = 'md',
  iconSize = 'md',
  shape = 'rounded',
  tooltip = 'top',
  surface = 'none',
  active = false,
  type = 'button',
  className,
  ...rest
}: IconButtonProps) {
  const classes = cx(
    'root',
    size,
    tone,
    ICON_SIZE_CLASS[iconSize],
    { circle: shape === 'circle', translucent: surface === 'translucent', active },
    className
  );

  const button = (
    <MuiIconButton
      className={classes}
      type={type}
      aria-label={label}
      aria-pressed={active || undefined}
      {...rest}
    >
      {icon}
    </MuiIconButton>
  );

  if (!tooltip) {
    return button;
  }

  return (
    <Tooltip
      title={label}
      placement={tooltip}
      arrow
    >
      {button}
    </Tooltip>
  );
}

IconButton.displayName = 'IconButton';
export default IconButton;
